/**
 * Générateur — Attendu 663 — Construire un angle de mesure donnée (6ème, cluster 32).
 *
 * 5 exercices / 5 formats :
 *   - deplacements_mesures_qcm       : QCM 4 choix — déplacement (quart/demi/
 *                                      tour complet/trois quarts) ↔ mesure.
 *   - etapes_construction_assoc      : association/ordonner — remettre dans
 *                                      l'ordre les 5 étapes de construction
 *                                      d'un angle au rapporteur.
 *   - construction_angle_programme   : programme_construction interactif — à
 *                                      l'aide du rapporteur, l'élève place +
 *                                      oriente + clique la graduation pour
 *                                      tracer la seconde demi-droite.
 *   - decomposition_saisie           : saisie — décomposition d'une mesure
 *                                      en somme/différence d'angles canoniques
 *                                      (V1: 90°+x, V2: 180°-x, V3: 2×(x/2)).
 *   - verification_qualitative_vf    : Vrai/Faux (3V+3F) + figure SVG — vérifier
 *                                      la cohérence d'un angle construit
 *                                      (aigu/obtus/droit/plat, pièges 180-m
 *                                      et m+90).
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_663.md et docs/fiches/PAYLOADS_663.md.
 */

import {
    randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeOrdonner, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 663;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEPLACEMENTS    = 'Déplacements et mesures';
const L_ETAPES          = 'Étapes de construction';
const L_CONSTRUCTION    = 'Construire un angle';
const L_DECOMPOSITION   = 'Décomposition canonique';
const L_VERIFICATION    = 'Vérification qualitative';

// Lettres valides — exclut I (confusion avec 1) et O (réservé au sommet xÔy).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Couleurs cohérentes cluster 31→32 (cf. gen_661, gen_662).
const COLOR_BLEU  = '#378ADD';
const COLOR_GRIS  = '#666666';

// Hauteur 240 et sommet centré à y = 120 : la figure est tournée sur 360°, chaque
// élément décrit donc un cercle autour du sommet. Le plus éloigné est une
// étiquette de demi-droite à 100,7 px, soit 111 px en comptant la hauteur du
// texte. Avec l'ancien cadre (300×200, sommet y = 150) il ne restait que 50 px
// sous le sommet et la figure sortait massivement.
const VIEWBOX_EX6 = { w: 300, h: 240 }; // Ex. 6 angle construit simple

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS (alignés sur gen_661.js / gen_662.js)
// ═══════════════════════════════════════════════════════════

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Sérialise une string SVG en data URL utf8. */
const svgToDataUrl = (svgString) =>
    `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;

/**
 * Construit l'attribut `d` d'un `<path>` pour un arc circulaire, de l'angle
 * `a1Deg` à `a2Deg` (convention mathématique : 0° = est, 90° = nord),
 * centré en (cx, cy), de rayon `rayon`. `sweep = 0` dessine l'arc à l'intérieur
 * de l'angle a1→a2.
 */
const arcD = (cx, cy, rayon, a1Deg, a2Deg) => {
    const a1 = (a1Deg * Math.PI) / 180;
    const a2 = (a2Deg * Math.PI) / 180;
    const x1 = cx + rayon * Math.cos(a1);
    const y1 = cy - rayon * Math.sin(a1);
    const x2 = cx + rayon * Math.cos(a2);
    const y2 = cy - rayon * Math.sin(a2);
    const deltaAbs = ((a2Deg - a1Deg) % 360 + 360) % 360;
    const largeArc = deltaAbs > 180 ? 1 : 0;
    const sweep = 0;
    return `M ${x1.toFixed(1)},${y1.toFixed(1)} A ${rayon} ${rayon} 0 ${largeArc} ${sweep} ${x2.toFixed(1)},${y2.toFixed(1)}`;
};

/** Décale `pt` de `decal` px dans la direction opposée à `vers`. */
const labelPosAt = (pt, vers, decal = 10) => {
    const dx = pt.x - vers.x;
    const dy = pt.y - vers.y;
    const n = Math.hypot(dx, dy);
    if (n < 1e-3) return { x: pt.x, y: pt.y };
    return { x: pt.x + (dx / n) * decal, y: pt.y + (dy / n) * decal };
};

/** Construit un `<text>` SVG simple (étiquette). */
const svgLabel = (text, x, y, { size = 13, italic = false, anchor = 'start', color = null } = {}) =>
    `<text x="${x.toFixed(0)}" y="${y.toFixed(0)}" font-family="serif" font-size="${size}"`
    + (italic ? ' font-style="italic"' : '')
    + (anchor !== 'start' ? ` text-anchor="${anchor}"` : '')
    + (color ? ` fill="${color}"` : '')
    + `>${text}</text>`;

/**
 * Ouvre un `<svg>` avec un attribut `data-v="{salt}"` placé AVANT `xmlns` pour
 * garantir que la variabilité tombe dans les 80 premiers caractères (fingerprint
 * auditGen).
 */
const svgOpen = (vb, salt) =>
    `<svg data-v="${salt}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}">`;

/** Salt dérivé de paramètres variants (numeric ou string). */
const makeSalt = (...parts) => parts.map(p => typeof p === 'number' ? Math.round(p) : String(p)).join('_');

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Déplacements et mesures d'angles (QCM)
// ═══════════════════════════════════════════════════════════

const DEPLACEMENTS = [
    { nom: 'un quart de tour',      mesure: 90 },
    { nom: 'un demi-tour',          mesure: 180 },
    { nom: 'un tour complet',       mesure: 360 },
    { nom: 'trois quarts de tour',  mesure: 270 },
];

const POOL_DIRECT = [0, 45, 60, 90, 120, 135, 180, 270, 360];

// Énoncés sens direct (déplacement → mesure)
const ENONCES_DIRECT = [
    (dep) => `<p>Combien de degrés mesure ${dep.nom} ?</p>`,
    (dep) => `<p>Quelle est la mesure, en degrés, de ${dep.nom} ?</p>`,
    (dep) => `<p>À combien de degrés correspond ${dep.nom} ?</p>`,
];

// Variante paramétrée par lettre contextuelle
const ENONCE_DIRECT_LETTRE = (dep, lhat) =>
    `<p>En tournant de ${dep.nom}, l'angle ${lhat} ainsi formé mesure combien de degrés ?</p>`;

// Énoncés sens inverse (mesure → déplacement), TOUS paramétrés par une
// lettre contextuelle pour multiplier les fingerprints uniques (4 déplacements
// × 3 énoncés × 23 lettres = 276 fingerprints inverses).
const ENONCES_INVERSE = [
    (m, lhat) => `<p>L'angle ${lhat} mesure ${m}°. À quel déplacement correspond-il ?</p>`,
    (m, lhat) => `<p>Un angle ${lhat} de ${m}° correspond à quel déplacement ?</p>`,
    (m, lhat) => `<p>À quel déplacement correspond l'angle ${lhat} mesurant ${m}° ?</p>`,
];

const genDeplacementsMesuresQcm = () => {
    const sens = Math.random() < 0.7 ? 'direct' : 'inverse';

    if (sens === 'direct') {
        const dep = randomChoice(DEPLACEMENTS);
        const bonne = dep.mesure;

        // 4 variantes (3 simples + 1 avec lettre contextuelle)
        const useLettre = Math.random() < 0.4;
        let texte;
        if (useLettre) {
            const L = randomChoice(LETTRES);
            texte = ENONCE_DIRECT_LETTRE(dep, `$\\widehat{${L}}$`);
        } else {
            texte = randomChoice(ENONCES_DIRECT)(dep);
        }

        // Distracteurs : priorité aux autres mesures canoniques {90, 180, 270, 360}
        // pour maximiser le contraste pédagogique.
        const autresCanoniques = DEPLACEMENTS
            .filter(d => d.mesure !== bonne)
            .map(d => d.mesure);
        const secondaires = POOL_DIRECT.filter(v => v !== bonne && !autresCanoniques.includes(v));

        // Prendre 2 canoniques + 1 secondaire pour garantir contraste.
        const dCano = randomChoiceMultiple(autresCanoniques, Math.min(2, autresCanoniques.length));
        const dSec = randomChoiceMultiple(secondaires, 3 - dCano.length);
        const distracteurs = [...dCano, ...dSec];

        const choix = [`${bonne}°`, ...distracteurs.map(d => `${d}°`)];
        const correct = `${bonne}°`;

        const payload = makeQcm(choix, correct, texte, opts(L_DEPLACEMENTS));
        payload._meta.generatorKey = 'deplacements_mesures_qcm';
        return payload;
    }

    // Sens inverse : mesure donnée → déplacement à trouver.
    // Paramétrage lettre systématique pour multiplier les fingerprints
    // (sinon 4 × 2 = 8 fingerprints → collisions quasi-garanties sur 6 tirages).
    const dep = randomChoice(DEPLACEMENTS);
    const m = dep.mesure;
    const bonne = dep.nom;

    const L = randomChoice(LETTRES);
    const lhat = `$\\widehat{${L}}$`;
    const enonceFn = randomChoice(ENONCES_INVERSE);
    const texte = enonceFn(m, lhat);

    // Choix = les 4 déplacements (4 noms uniques, couvre toutes les bonnes réponses possibles)
    const autres = DEPLACEMENTS.filter(d => d.nom !== bonne).map(d => d.nom);
    const choix = [bonne, ...autres];

    const payload = makeQcm(choix, bonne, texte, opts(L_DEPLACEMENTS));
    payload._meta.generatorKey = 'deplacements_mesures_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Ordre des étapes de construction (association/ordonner)
// ═══════════════════════════════════════════════════════════

const MESURES_EX2 = [30, 45, 60, 75, 100, 110, 120, 135, 150, 160];

// Convention 100% ABĈ pour garantir ≥ 20 fingerprints uniques
// (10 626 triplets lettres × 10 mesures × 3 énoncés = 318 780 fingerprints).
const etapesABC = (S, A, B, m) => [
    `Tracer une demi-droite [${S}${A}).`,
    `Placer le centre du rapporteur sur le sommet ${S}.`,
    `Aligner la graduation 0° du rapporteur sur la demi-droite [${S}${A}).`,
    `Repérer la graduation correspondant à ${m}°.`,
    `Tracer la demi-droite [${S}${B}) en partant du sommet ${S} vers la graduation repérée.`,
];

const ENONCES_EX2 = [
    (notation, m) => `<p>Voici les 5 étapes de la construction de l'angle ${notation} de mesure ${m}° au rapporteur. Remets-les dans le bon ordre.</p>`,
    (notation, m) => `<p>Pour construire l'angle ${notation} de ${m}° au rapporteur, on suit 5 étapes. Remets-les dans l'ordre.</p>`,
    (notation, m) => `<p>Remets dans l'ordre les 5 étapes de construction de l'angle ${notation} = ${m}°.</p>`,
];

const genEtapesConstructionAssoc = () => {
    const m = randomChoice(MESURES_EX2);
    const [L1, S, L2] = pickN(3);
    const notation = `$\\widehat{${L1}${S}${L2}}$`;

    const items = etapesABC(S, L1, L2, m);
    const enonceFn = randomChoice(ENONCES_EX2);
    const texte = enonceFn(notation, m);

    const payload = makeOrdonner(items, texte, opts(L_ETAPES));
    payload._meta.generatorKey = 'etapes_construction_assoc';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Construction d'un angle (programme_construction interactif)
// ═══════════════════════════════════════════════════════════

const MESURES_EX4 = [30, 45, 50, 60, 75, 100, 110, 120, 130, 150, 160];

const genConstructionAngleProgramme = () => {
    const m = randomChoice(MESURES_EX4);
    // Triplet (L1, S, L2) = côté déjà tracé, sommet, côté à construire.
    const [L1, S, L2] = pickN(3);
    const notation = `$\\widehat{${L1}${S}${L2}}$`;

    // Vertex au centre-bas du viewBox, ray1 horizontal vers la droite (angleDeg=0).
    // Angles arrondis à l'entier pour cohérence avec le snap au degré du rapporteur.
    const VX = 200, VY = 280;
    const RAY_LEN = 200;

    // Le mode d'emploi du rapporteur (poser le centre, aligner la base, cliquer
    // la graduation) est passé dans le coup de pouce : l'élève qui sait faire
    // n'a pas à le relire, celui qui bloque va le chercher.
    const texte = `<p>Sur la figure, construis l'angle ${notation} tel que ${notation} = <b>${m}°</b>.</p>`;

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: 30,
            tolerance: 12,
            viewBox: { w: 620, h: 420 },
            points: [{ name: S, x: VX, y: VY }],
            preShapes: [
                { type: 'ray', from: S, angleDeg: 0, length: RAY_LEN, labelEnd: L1 },
            ],
            tasks: [
                {
                    id: 'angle',
                    kind: 'angle_ray',
                    consigne: `Trace en <b>vert</b> la demi-droite [${S}${L2}) telle que l'angle ${notation} mesure ${m}°.`,
                    color: '#16a34a',
                    tolerance_deg: 2,
                    shape: { type: 'ray', from: S, angleDeg: m, labelEnd: L2 },
                },
            ],
        },
        texte,
        opts(L_CONSTRUCTION),
    );
    payload._meta.generatorKey = 'construction_angle_programme';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Décomposition en angles canoniques (saisie)
// ═══════════════════════════════════════════════════════════

const MESURES_V1 = [100, 105, 110, 115, 120, 130, 135, 140, 150, 160, 170]; // M > 90
const MESURES_V2 = [30, 45, 50, 60, 70, 75, 80, 100, 110, 120, 130, 135, 150, 160, 170]; // M < 180
const MESURES_V3 = [40, 50, 60, 70, 80, 100, 110, 120, 130, 140, 150, 160]; // M pair, < 180

// 2 formulations par régime — paramétrées par une lettre `lhat` (nom de
// l'angle à construire) pour multiplier les fingerprints (23 lettres × 2
// formulations × pool mesures ≈ 506-690 fingerprints par régime).
const ENONCES_V1 = [
    (M, lhat) => `<p>Pour construire l'angle ${lhat} de ${M}°, je trace d'abord un angle droit (90°) puis j'ajoute un second angle. Quelle est la mesure de cet angle à ajouter (en degrés) ?</p>`,
    (M, lhat) => `<p>On trace un angle droit (90°), puis on ajoute un angle adjacent pour obtenir l'angle ${lhat} de ${M}°. Quelle est la mesure de cet angle adjacent ? Saisis le nombre de degrés.</p>`,
];
const ENONCES_V2 = [
    (M, lhat) => `<p>Pour construire l'angle ${lhat} de ${M}°, je trace un angle plat (180°) puis je retire un angle. Quelle est la mesure de cet angle à retirer (en degrés) ?</p>`,
    (M, lhat) => `<p>On part d'un angle plat (180°) et on retire un angle pour obtenir l'angle ${lhat} de ${M}°. Quelle est la mesure de l'angle retiré ? Saisis le nombre de degrés.</p>`,
];
const ENONCES_V3 = [
    (M, lhat) => `<p>Pour construire l'angle ${lhat} de ${M}°, j'additionne deux angles aigus de même mesure. Quelle est la mesure de chacun (en degrés) ?</p>`,
    (M, lhat) => `<p>L'angle ${lhat} mesure ${M}° et peut s'obtenir en accolant deux angles aigus égaux. Quelle est la mesure d'un de ces deux angles ? Saisis le nombre de degrés.</p>`,
];

const genDecompositionSaisie = () => {
    const regime = randomChoice(['V1', 'V2', 'V3']);
    let M, reponse, enonces;
    if (regime === 'V1') {
        M = randomChoice(MESURES_V1);
        reponse = M - 90;
        enonces = ENONCES_V1;
    } else if (regime === 'V2') {
        M = randomChoice(MESURES_V2);
        reponse = 180 - M;
        enonces = ENONCES_V2;
    } else {
        M = randomChoice(MESURES_V3);
        reponse = M / 2;
        enonces = ENONCES_V3;
    }

    const formulation = randomChoice(enonces);

    // Paramétrage lettre systématique : le nom de l'angle à construire est
    // `L̂` (lettre aléatoire dans LETTRES). Garantit ≥ 23 variantes par
    // (régime, formulation, M) → pas de collision sur 20 itérations
    // (23 × 2 × pool ≥ 506 fingerprints par régime).
    const L = randomChoice(LETTRES);
    const lhat = `$\\widehat{${L}}$`;

    const texte = formulation(M, lhat);

    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_DECOMPOSITION),
        strict: true,
        suffixe: '°',
    });
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: regime };
    payload._meta.generatorKey = 'decomposition_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Vérification qualitative (Vrai/Faux + SVG)
// ═══════════════════════════════════════════════════════════

const MESURES_EX6 = [35, 52, 68, 95, 112, 128, 145];

/**
 * Construit les listes d'affirmations V et F candidates pour une mesure donnée.
 * Chaque affirmation est une string ; les entrées paramétrées par m produisent
 * une valeur numérique différente par mesure.
 */
const buildAffirmationsEx6 = (m) => {
    // Affirmations universellement fausses (pour ce pool m ∉ {0, 90, 180, 360}).
    const F_universel = [
        'Cet angle est droit.',
        'Cet angle est plat.',
        `Cet angle mesure ${180 - m}°.`,
        `Cet angle mesure ${m + 90}°.`,
        'Cet angle mesure un tour complet.',
        'Cet angle est nul.',
    ];

    if (m < 90) {
        // Aigu
        return {
            V: [
                'Cet angle est aigu.',
                'Cet angle mesure moins qu\'un quart de tour.',
                'Cet angle est plus petit qu\'un angle droit.',
                'Cet angle mesure moins qu\'un demi-tour.',
            ],
            F: [
                ...F_universel,
                'Cet angle est obtus.',
                'Cet angle mesure plus qu\'un quart de tour.',
            ],
        };
    }
    // Obtus (m > 90 et m < 180)
    return {
        V: [
            'Cet angle est obtus.',
            'Cet angle mesure plus qu\'un quart de tour.',
            'Cet angle est plus grand qu\'un angle droit.',
            'Cet angle mesure moins qu\'un demi-tour.',
        ],
        F: [
            ...F_universel,
            'Cet angle est aigu.',
            'Cet angle mesure moins qu\'un quart de tour.',
        ],
    };
};

/** SVG d'un angle construit pour Ex. 6 (2 demi-droites + arc, pas de mesure affichée). */
const buildAngleConstruitSvg = (m, rotationDeg, labels, S) => {
    const Ox = 150, Oy = 120, len = 90;
    const toRad = (d) => (d * Math.PI) / 180;

    const dir1 = rotationDeg;
    const dir2 = rotationDeg + m;
    const ptAt = (angDeg) => ({
        x: Ox + len * Math.cos(toRad(angDeg)),
        y: Oy - len * Math.sin(toRad(angDeg)),
    });
    const P1 = ptAt(dir1);
    const P2 = ptAt(dir2);

    const lbl1 = labelPosAt(P1, { x: Ox, y: Oy }, 10);
    const lbl2 = labelPosAt(P2, { x: Ox, y: Oy }, 10);

    const salt = makeSalt(m, Math.round(rotationDeg), S);
    return svgOpen(VIEWBOX_EX6, salt)
        + `<line x1="${Ox}" y1="${Oy}" x2="${P1.x.toFixed(1)}" y2="${P1.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${Ox}" y1="${Oy}" x2="${P2.x.toFixed(1)}" y2="${P2.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<path d="${arcD(Ox, Oy, 20, dir1, dir2)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`
        + `<circle cx="${Ox}" cy="${Oy}" r="2.5" fill="black"/>`
        + svgLabel(labels[0], lbl1.x, lbl1.y)
        + svgLabel(labels[1], lbl2.x, lbl2.y)
        + svgLabel(S, Ox + 6, Oy + 14)
        + '</svg>';
};

// Pas de « pour chaque affirmation, indique si elle est vraie ou fausse » :
// le tableau Vrai/Faux le dit déjà.
const ENONCES_EX6 = [
    (notation, m) => `<p>On a construit au rapporteur un angle ${notation} dont la figure est affichée ci-dessous. La mesure de cet angle est ${m}°.</p>`,
    (notation, m) => `<p>L'angle ${notation} ci-dessous a été construit au rapporteur et mesure ${m}°.</p>`,
];

const genVerificationQualitativeVf = () => {
    const m = randomChoice(MESURES_EX6);
    const { V: Vcand, F: Fcand } = buildAffirmationsEx6(m);

    // Tirer 3 V + 3 F
    const vChoisies = randomChoiceMultiple(Vcand, 3);
    const fChoisies = randomChoiceMultiple(Fcand, 3);

    // Mélanger les 6 pour afficher dans un ordre variable
    const melange = shuffle([...vChoisies, ...fChoisies]);
    const lignes = melange;
    const bonnesReponses = lignes.map(l => vChoisies.includes(l) ? 0 : 1);

    // Lettres pour la notation et le sommet
    const [L1, S, L2] = pickN(3);
    const notation = `$\\widehat{${L1}${S}${L2}}$`;

    // SVG
    const rotationDeg = Math.random() * 360;
    const svg = buildAngleConstruitSvg(m, rotationDeg, [L1, L2], S);
    const imageData = svgToDataUrl(svg);

    const enonceFn = randomChoice(ENONCES_EX6);
    const texte = enonceFn(notation, m);

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_VERIFICATION),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'verification_qualitative_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    deplacements_mesures_qcm:       genDeplacementsMesuresQcm,
    etapes_construction_assoc:      genEtapesConstructionAssoc,
    construction_angle_programme:   genConstructionAngleProgramme,
    decomposition_saisie:           genDecompositionSaisie,
    verification_qualitative_vf:    genVerificationQualitativeVf,
};

export const CATEGORIES = [
    { id: 'deplacements_mesures',     label: L_DEPLACEMENTS,  formatsList: [{ key: 'deplacements_mesures_qcm',       label: 'QCM' }] },
    { id: 'etapes_construction',      label: L_ETAPES,        formatsList: [{ key: 'etapes_construction_assoc',      label: 'Ordonner les étapes' }] },
    { id: 'construction_angle',       label: L_CONSTRUCTION,  formatsList: [{ key: 'construction_angle_programme',   label: 'Construction interactive' }] },
    { id: 'decomposition',            label: L_DECOMPOSITION, formatsList: [{ key: 'decomposition_saisie',           label: 'Saisie' }] },
    { id: 'verification_qualitative', label: L_VERIFICATION,  formatsList: [{ key: 'verification_qualitative_vf',    label: 'Vrai/Faux + figure' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genDeplacementsMesuresQcm,
    genEtapesConstructionAssoc,
    genConstructionAngleProgramme,
    genDecompositionSaisie,
    genVerificationQualitativeVf,
    buildAngleConstruitSvg,
};
